import React, { FC, useCallback, useEffect, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import { withWindow } from '@shared/utils/withWindow';

type GeneralInfoBannerProviderContext = {
  isGeneralInfoBannerVisible: boolean;
  updateGeneralInfoBannerVisibility: (isVisible: boolean) => void;
  generalInfoBannerHeight: number;
  calculateGeneralInfoBannerHeight: () => void;
  bannerProps: BannerProps;
  updateBannerProps: (props: BannerProps) => void;
};

type BannerProps = {
  content: string;
  icon?: React.ReactNode;
};

const [Provider, useGeneralInfoBannerProvider] = createContext<GeneralInfoBannerProviderContext>({
  name: 'GeneralInfoBannerProvider'
});

const GeneralInfoBannerProvider: FC = ({ children }) => {
  const [isGeneralInfoBannerVisible, setIsGeneralInfoBannerVisible] = useState(false);
  const [generalInfoBannerHeight, setGeneralInfoBannerHeight] = useState(0);

  const [bannerProps, setBannerProps] = useState<BannerProps>({
    content: '',
    icon: undefined
  });

  const calculateGeneralInfoBannerHeight = useCallback(() => {
    const generalInfoBanner = withWindow(() => document.getElementById('general-info-banner'), null);
    const height = generalInfoBanner?.clientHeight || 0;
    setGeneralInfoBannerHeight(height);
  }, []);

  useEffect(() => {
    calculateGeneralInfoBannerHeight();
    withWindow(window => {
      window.addEventListener('resize', calculateGeneralInfoBannerHeight);
    });
    return () => {
      withWindow(window => {
        window.removeEventListener('resize', calculateGeneralInfoBannerHeight);
      });
    };
  }, [calculateGeneralInfoBannerHeight]);

  return (
    <Provider
      value={{
        isGeneralInfoBannerVisible,
        updateGeneralInfoBannerVisibility: setIsGeneralInfoBannerVisible,
        generalInfoBannerHeight,
        calculateGeneralInfoBannerHeight,
        bannerProps,
        updateBannerProps: setBannerProps
      }}
    >
      {children}
    </Provider>
  );
};

export { GeneralInfoBannerProvider, useGeneralInfoBannerProvider };
